import { useCallback, useEffect, useRef, useState } from "react";

import Image from "next/image";

import type { Control, FieldValues, Path } from "react-hook-form";
import { useController } from "react-hook-form";

import passengerIcon from "@repo/ui/assets/icons/passenger-white.svg";
import { Input } from "@repo/ui/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@repo/ui/components/ui/popover";
import { useMediaQuery } from "@repo/ui/Hooks/useMediaQuery";
import { cn } from "@repo/ui/lib/utils";

import { Counter } from "./Counter";

export interface GuestCounts {
  adults: number;
  teenagers: number;
  children: number;
  infants: number;
}

export interface ValidationResult {
  isValid: boolean;
  errorMessage: string;
}

export interface PassengerLimits {
  maxPassengers: number;
  maxInfants: number;
}

export type GuestSelectorPopoverProps<T extends FieldValues> = {
  control: Control<T>;
  name: Path<T>;
  disabled?: boolean;
  isGuestSelectorOpen: boolean;
  setShowGuestSelector: (val: boolean) => void;
  adultLabel?: string;
  teenagerLabel?: string;
  childLabel?: string;
  infantLabel?: string;
  adultDescriptionLabel?: string;
  teenagerDescriptionLabel?: string;
  childDescriptionLabel?: string;
  infantDescriptionLabel?: string;
  confirmLabel?: string;
};

const PASSENGER_LIMITS: PassengerLimits = {
  maxPassengers: 9,
  maxInfants: 9,
};

const DEFAULT_GUESTS: GuestCounts = { adults: 1, teenagers: 0, children: 0, infants: 0 };

const hasSameCounts = (a?: GuestCounts, b?: GuestCounts) =>
  (a?.adults ?? 0) === (b?.adults ?? 0) &&
  (a?.teenagers ?? 0) === (b?.teenagers ?? 0) &&
  (a?.children ?? 0) === (b?.children ?? 0) &&
  (a?.infants ?? 0) === (b?.infants ?? 0);

export function GuestSelectorPopover<T extends FieldValues>({
  control,
  name,
  disabled = false,
  isGuestSelectorOpen = false,
  setShowGuestSelector,
  adultLabel = "Adults",
  teenagerLabel = "Teenagers",
  childLabel = "Children",
  infantLabel = "Infants",
  adultDescriptionLabel = "18 o más años",
  teenagerDescriptionLabel = "14 - 17 años",
  childDescriptionLabel = "2 - 13 años",
  infantDescriptionLabel = "0 - 23 meses",
  confirmLabel = "Confirmar",
}: Readonly<GuestSelectorPopoverProps<T>>) {
  const PASSENGER_CONFIG = {
    adults: { label: adultLabel, desc: adultDescriptionLabel, minAge: 18 },
    teenagers: { label: teenagerLabel, desc: teenagerDescriptionLabel, minAge: 13, maxAge: 17 },
    children: { label: childLabel, desc: childDescriptionLabel, minAge: 2, maxAge: 12 },
    infants: { label: infantLabel, desc: infantDescriptionLabel, minAge: 0, maxAge: 23 },
  };

  const isDesktop = useMediaQuery("(min-width: 768px)");
  const [openState, setOpenState] = useState(isGuestSelectorOpen);

  const {
    field: { value, onChange },
    fieldState,
  } = useController({ name, control });

  const [guests, setGuests] = useState<GuestCounts>(value ?? DEFAULT_GUESTS);

  const syncedValueRef = useRef<GuestCounts>(value ?? DEFAULT_GUESTS);

  useEffect(() => {
    if (!value || hasSameCounts(value, syncedValueRef.current)) return;

    syncedValueRef.current = value;
    setGuests(value);
  }, [value]);

  const passengerCounts = {
    adults: guests.adults,
    teenagers: guests.teenagers ?? 0,
    children: guests.children,
    infants: guests.infants,
  };

  const totalPassengers =
    passengerCounts.adults + passengerCounts.teenagers + passengerCounts.children;
  const totalGuardians = passengerCounts.adults + passengerCounts.teenagers;
  const hasRequiredGuardian = passengerCounts.adults >= 1 || passengerCounts.teenagers >= 1;

  const validateGuestCounts = (counts: typeof passengerCounts): ValidationResult => {
    if (!hasRequiredGuardian) {
      return {
        isValid: false,
        errorMessage: "Require at least 1 adult or exactly 1 teenager.",
      };
    }

    if (totalPassengers > PASSENGER_LIMITS.maxPassengers) {
      return {
        isValid: false,
        errorMessage: `Maximum ${PASSENGER_LIMITS.maxPassengers} passengers (adults + teenagers + children).`,
      };
    }

    if (counts.infants > PASSENGER_LIMITS.maxInfants) {
      return {
        isValid: false,
        errorMessage: `Maximum ${PASSENGER_LIMITS.maxInfants} infants.`,
      };
    }

    if (counts.infants > totalGuardians) {
      return {
        isValid: false,
        errorMessage: "1 infante por adulto o adolescente.",
      };
    }

    return { isValid: true, errorMessage: "" };
  };

  const validation = validateGuestCounts(passengerCounts);

  const getPassengerLimits = () => {
    const remainingPassengerSlots = PASSENGER_LIMITS.maxPassengers - totalPassengers;
    const minAdultsRequired = passengerCounts.teenagers > 0 ? 0 : 1;

    return {
      adults: {
        canIncrement: remainingPassengerSlots > 0,
        canDecrement:
          passengerCounts.adults > minAdultsRequired &&
          !(
            passengerCounts.adults === 1 &&
            passengerCounts.teenagers === 0 &&
            passengerCounts.children === 0 &&
            passengerCounts.infants === 0
          ),
        min: minAdultsRequired,
        max: PASSENGER_LIMITS.maxPassengers - passengerCounts.children - passengerCounts.teenagers,
      },
      teenagers: {
        canIncrement: remainingPassengerSlots > 0,
        canDecrement:
          passengerCounts.teenagers > 0 &&
          !(
            passengerCounts.adults === 0 &&
            passengerCounts.teenagers === 1 &&
            (passengerCounts.children > 0 || passengerCounts.infants > 0)
          ) &&
          !(
            passengerCounts.adults === 0 &&
            passengerCounts.teenagers === 1 &&
            passengerCounts.children === 0 &&
            passengerCounts.infants === 0
          ),
        min: 0,
        max: PASSENGER_LIMITS.maxPassengers - passengerCounts.adults - passengerCounts.children,
      },
      children: {
        canIncrement:
          remainingPassengerSlots > 0 &&
          (passengerCounts.adults > 0 ||
            (passengerCounts.teenagers > 0 &&
              passengerCounts.children + passengerCounts.infants < passengerCounts.teenagers)),
        canDecrement: passengerCounts.children > 0,
        min: 0,
        max: Math.min(
          PASSENGER_LIMITS.maxPassengers - passengerCounts.adults - passengerCounts.teenagers,
          passengerCounts.adults > 0 ? passengerCounts.adults * 8 : passengerCounts.teenagers,
        ),
      },
      infants: {
        canIncrement:
          passengerCounts.infants < totalGuardians &&
          passengerCounts.infants < PASSENGER_LIMITS.maxInfants &&
          (passengerCounts.adults > 0 ||
            (passengerCounts.teenagers > 0 &&
              passengerCounts.children + passengerCounts.infants < passengerCounts.teenagers)),
        canDecrement: passengerCounts.infants > 0,
        min: 0,
        max: Math.min(
          totalGuardians,
          PASSENGER_LIMITS.maxInfants,
          passengerCounts.adults > 0
            ? PASSENGER_LIMITS.maxInfants
            : passengerCounts.teenagers - passengerCounts.children,
        ),
      },
    };
  };

  const limits = getPassengerLimits();

  const updateGuestCount = (type: keyof GuestCounts) => (newValue: number) => {
    setGuests((prevGuests) => {
      const updatedGuests = { ...prevGuests, [type]: newValue };

      if (type === "adults" || type === "teenagers") {
        const newTotalGuardians = (updatedGuests.adults ?? 0) + (updatedGuests.teenagers ?? 0);
        updatedGuests.infants = Math.min(updatedGuests.infants, newTotalGuardians);
      }

      if (type === "adults") {
        const newAdultCount = newValue;
        const teenCount = updatedGuests.teenagers ?? 0;

        let maxChildrenAllowed;
        if (newAdultCount > 0) {
          maxChildrenAllowed = newAdultCount * 8;
        } else if (teenCount > 0) {
          maxChildrenAllowed = Math.min(teenCount, teenCount);
        } else {
          maxChildrenAllowed = 0;
        }

        updatedGuests.children = Math.min(updatedGuests.children, maxChildrenAllowed);

        if (newAdultCount === 0) {
          if (teenCount > 0) {
            const totalDependents = updatedGuests.children + updatedGuests.infants;
            if (totalDependents > teenCount) {
              let excess = totalDependents - teenCount;

              const infantsToRemove = Math.min(excess, updatedGuests.infants);
              updatedGuests.infants -= infantsToRemove;
              excess -= infantsToRemove;

              if (excess > 0) {
                const childrenToRemove = Math.min(excess, updatedGuests.children);
                updatedGuests.children -= childrenToRemove;
                excess -= childrenToRemove;
              }

              if (excess > 0 && updatedGuests.teenagers > 0) {
                updatedGuests.teenagers = Math.max(0, updatedGuests.teenagers - excess);
              }
            }
          }
        }
      }

      if (type === "teenagers") {
        const newTeenCount = newValue;
        const adultCount = updatedGuests.adults ?? 0;

        if (adultCount === 0 && newTeenCount > 0) {
          const totalDependents = updatedGuests.children + updatedGuests.infants;
          const maxDependents = newTeenCount;

          if (totalDependents > maxDependents) {
            const excess = totalDependents - maxDependents;
            if (updatedGuests.infants > 0) {
              const infantsToRemove = Math.min(excess, updatedGuests.infants);
              updatedGuests.infants -= infantsToRemove;
              const remainingExcess = excess - infantsToRemove;
              if (remainingExcess > 0) {
                updatedGuests.children = Math.max(0, updatedGuests.children - remainingExcess);
              }
            } else {
              updatedGuests.children = Math.max(0, updatedGuests.children - excess);
            }
          }
        }
      }

      const totalAfterUpdate =
        (updatedGuests.adults ?? 0) +
        (updatedGuests.teenagers ?? 0) +
        updatedGuests.children +
        updatedGuests.infants;
      if (totalAfterUpdate === 0) {
        updatedGuests.adults = 1;
      }

      return updatedGuests;
    });
  };

  const generateSummary = (): string => {
    const parts = [];

    if (passengerCounts.adults > 0) {
      parts.push(`${passengerCounts.adults} ${adultLabel}`);
    }
    if (passengerCounts.teenagers > 0) {
      parts.push(`${passengerCounts.teenagers} ${teenagerLabel}`);
    }
    if (passengerCounts.children > 0) {
      parts.push(`${passengerCounts.children} ${childLabel}`);
    }
    if (passengerCounts.infants > 0) {
      parts.push(`${passengerCounts.infants} ${infantLabel}`);
    }

    return parts.join(", ") || `1 ${adultLabel}`;
  };

  const handleConfirm = () => {
    if (!validation.isValid) return;

    onChange(guests);
    setShowGuestSelector?.(false);
    setOpenState(false);
  };

  const handleOpenChange = useCallback(
    (val: boolean) => {
      if (!disabled) {
        setShowGuestSelector?.(val);
        setOpenState(val);
      }
    },
    [disabled, setShowGuestSelector],
  );

  const open = typeof isGuestSelectorOpen === "boolean" ? isGuestSelectorOpen : openState;

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild data-testid="guest-selector-btn">
        <div
          className={cn(
            "border-brand-black-60 hover:border-brand-blue md:max-w-auto flex h-10 flex-1 items-center gap-2 rounded-md border px-3",
            disabled && "pointer-events-none cursor-not-allowed opacity-50",
          )}
        >
          <Input
            readOnly
            value={generateSummary()}
            placeholder={`1 ${adultLabel}`}
            className={cn(
              "line-height-none h-auto w-full rounded-none border-0 p-0 shadow-none focus-visible:ring-0 focus-visible:ring-offset-0",
              !disabled && "cursor-pointer",
            )}
            data-testid="guest-selector-input"
          />
          <Image
            src={passengerIcon}
            alt="Search"
            width={20}
            height={20}
            className="cursor-pointer"
          />
        </div>
      </PopoverTrigger>

      <PopoverContent className="p-4" align={isDesktop ? "end" : "start"}>
        <div className="space-y-4">
          <Counter
            label={PASSENGER_CONFIG.adults.label}
            desc={PASSENGER_CONFIG.adults.desc}
            value={passengerCounts.adults}
            onChange={updateGuestCount("adults")}
            min={limits.adults.min}
            max={limits.adults.max}
            canIncrement={limits.adults.canIncrement}
            canDecrement={limits.adults.canDecrement}
            testId="adults-counter"
          />

          <Counter
            label={PASSENGER_CONFIG.teenagers.label}
            desc={PASSENGER_CONFIG.teenagers.desc}
            value={passengerCounts.teenagers}
            onChange={updateGuestCount("teenagers")}
            min={limits.teenagers.min}
            max={limits.teenagers.max}
            canIncrement={limits.teenagers.canIncrement}
            canDecrement={limits.teenagers.canDecrement}
            testId="teenagers-counter"
          />

          <Counter
            label={PASSENGER_CONFIG.children.label}
            desc={PASSENGER_CONFIG.children.desc}
            value={passengerCounts.children}
            onChange={updateGuestCount("children")}
            min={limits.children.min}
            max={limits.children.max}
            canIncrement={limits.children.canIncrement}
            canDecrement={limits.children.canDecrement}
            testId="children-counter"
          />

          <Counter
            label={PASSENGER_CONFIG.infants.label}
            desc={PASSENGER_CONFIG.infants.desc}
            value={passengerCounts.infants}
            onChange={updateGuestCount("infants")}
            min={limits.infants.min}
            max={limits.infants.max}
            canIncrement={limits.infants.canIncrement}
            canDecrement={limits.infants.canDecrement}
            testId="infants-counter"
          />
        </div>

        <button
          className={cn(
            "hover:bg-brand-blue mt-6 flex w-full cursor-pointer items-center justify-center rounded-full bg-cyan-500 py-3 text-sm font-semibold text-white transition-colors",
            !validation.isValid && "cursor-not-allowed opacity-50",
          )}
          onClick={handleConfirm}
          type="button"
          disabled={!validation.isValid}
          data-testid="guest-selector-confirm-btn"
        >
          {confirmLabel}{" "}
          <span className="ml-2">
            <svg
              width="13"
              height="14"
              viewBox="0 0 13 14"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M6.09375 3.84961L8.96289 6.69336C9.08138 6.81185 9.14062 6.95573 9.14062 7.125C9.14062 7.29427 9.08138 7.43815 8.96289 7.55664L6.11914 10.4004C5.83138 10.6374 5.54362 10.6374 5.25586 10.4004C5.01888 10.1126 5.01888 9.82487 5.25586 9.53711L7.66797 7.125L5.23047 4.71289C4.99349 4.42513 4.99349 4.13737 5.23047 3.84961C5.51823 3.61263 5.80599 3.61263 6.09375 3.84961ZM6.5 0.625C7.71875 0.641927 8.81055 0.938151 9.77539 1.51367C10.7572 2.08919 11.5358 2.86784 12.1113 3.84961C12.6868 4.81445 12.9831 5.90625 13 7.125C12.9831 8.34375 12.6868 9.43555 12.1113 10.4004C11.5358 11.3822 10.7572 12.1608 9.77539 12.7363C8.81055 13.3118 7.71875 13.6081 6.5 13.625C5.28125 13.6081 4.18945 13.3118 3.22461 12.7363C2.24284 12.1608 1.46419 11.3822 0.888672 10.4004C0.313151 9.43555 0.0169271 8.34375 0 7.125C0.0169271 5.90625 0.313151 4.81445 0.888672 3.84961C1.46419 2.86784 2.24284 2.08919 3.22461 1.51367C4.18945 0.938151 5.28125 0.641927 6.5 0.625ZM6.5 12.4062C7.98958 12.3724 9.23372 11.8561 10.2324 10.8574C11.2311 9.85872 11.7474 8.61458 11.7812 7.125C11.7474 5.63542 11.2311 4.39128 10.2324 3.39258C9.23372 2.39388 7.98958 1.8776 6.5 1.84375C5.01042 1.8776 3.76628 2.39388 2.76758 3.39258C1.76888 4.39128 1.2526 5.63542 1.21875 7.125C1.2526 8.61458 1.76888 9.85872 2.76758 10.8574C3.76628 11.8561 5.01042 12.3724 6.5 12.4062Z"
                fill="white"
              />
            </svg>
          </span>
        </button>

        {validation.errorMessage && (
          <div className="mt-3 text-sm text-red-500">{validation.errorMessage}</div>
        )}

        {fieldState.error && (
          <div className="mt-2 text-sm text-red-500">{fieldState.error.message}</div>
        )}
      </PopoverContent>
    </Popover>
  );
}
